import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import {
  Button,
  Form,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIintegrationExchangeCodeToAccessToken,
  APIintegrationConnect,
  APIintegrationGetAccounts,
} from '../api/Integrations';
import { APIsendEmailLoginCode } from '../api/User';

import ExternalAuthAccountList from '../components/ExternalAuth/AccountList';
import ExternalAuthSignupForm from '../components/ExternalAuth/SignupForm';

import {
  getStateFromUrl,
  hideIntercomWidget,
} from '../helpers';
import { getLogoUrl } from '../helpers/App';
import { getSetting } from '../helpers/Settings';

import Loading from '../components/Loading';

const ExternalAuth = ({
  history,
  location,
  match,
}) => {
  const intl = useIntl();

  const urlParams = (new URLSearchParams(location.search));

  const [integration, setIntegration] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [email, setEmail] = useState(null);
  const [emailField, setEmailField] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [connectingUuid, setConnectingUuid] = useState(null);
  const [view, setView] = useState('main');
  const [backUrl, setBackUrl] = useState('');
  const [backText, setBackText] = useState('');

  const { serviceId } = match.params;
  const code = urlParams.get('code');
  const authToken = urlParams.get('token');

  const integrationShouldGetAccounts =
    serviceId.startsWith('adobe') ||
    ['figma', 'wordpress', 'canva', 'shopify', 'mcp-server'].includes(serviceId);

  useEffect(() => {
    hideIntercomWidget();
  }, []);

  const replaceCodeWithToken = useCallback((token) => {
    const params = new URLSearchParams(location.search);
    params.delete('code');
    params.set('token', token);
    history.replace(`${location.pathname}?${params.toString()}`);
  }, [history, location]);

  const exchangeCodeToAuthToken = useCallback(() => {
    if (code && serviceId) {
      setError(false);
      setLoading(true);
      APIintegrationExchangeCodeToAccessToken(serviceId, code).then((response) => {
        const { token } = response;
        if (token) {
          replaceCodeWithToken(token);
          setLoading(false);
        }
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
    }
  }, [serviceId, code, replaceCodeWithToken]);

  const getAccounts = useCallback((emailVerifyCode) => {
    if (serviceId && (authToken || emailVerifyCode || integrationShouldGetAccounts)) {
      setError(false);
      setErrorMessage(null);
      setLoading(true);
      APIintegrationGetAccounts(serviceId, emailVerifyCode).then((response) => {
        setIntegration(response.integration);
        setAccounts(response.accounts || []);
        setEmail(response?.email || null);
        let newView = 'main';
        if (!emailVerifyCode && !response.integration.email_authentication) {
          newView = 'email';
        }
        setView(urlParams.get('view') || newView);
        if (response.error) {
          setError(true);
          setErrorMessage(response.error);
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, authToken]);

  const sendEmailValidationCode = (e) => {
    e.preventDefault();
    setSending(true);
    APIsendEmailLoginCode(emailField).then(() => {
      setEmailField('');
      setEmailCode('');
      setView('code');
      setSending(false);
    });
  };

  const confirmCode = (e) => {
    e.preventDefault();
    if (emailCode.length < 6) {
      return;
    }
    getAccounts(emailCode);
  };

  useEffect(() => {
    if (serviceId) {
      exchangeCodeToAuthToken();
    }
  }, [serviceId, exchangeCodeToAuthToken]);

  useEffect(() => {
    if (integrationShouldGetAccounts || (serviceId && authToken)) {
      getAccounts();
    }
  }, [serviceId, authToken, getAccounts, integrationShouldGetAccounts]);

  const includeoAuthParameters = ['canva', 'mcp-server'].includes(serviceId);

  const handleConnect = (accountUuid) => {
    setConnectingUuid(accountUuid);
    APIintegrationConnect(serviceId, {
      account_uuid: accountUuid,
      token: authToken,
      ...(!includeoAuthParameters && { request_id: getStateFromUrl()?.request_id }),
      ...(includeoAuthParameters && { state: urlParams.get('state') }),
      ...(includeoAuthParameters && { client_id: urlParams.get('client_id') }),
      ...(['mcp-server'].includes(serviceId) && { redirect_uri: urlParams.get('redirect_uri') }),
      ...(includeoAuthParameters && { code_challenge: urlParams.get('code_challenge') }),
    }).then((response) => {
      if (response.token) {
        if (response.token.authentication_back_url) {
          setBackUrl(response.token.authentication_back_url);
          setBackText(response.token.authentication_back_text);
        } else if (integration.authentication_back_url) {
          setBackUrl(integration.authentication_back_url);
          setBackText(integration.authentication_back_text);
        } else if (integration.connect_back_url) {
          const connectBackUrl = new URL(integration.connect_back_url);
          connectBackUrl.searchParams.set('token', response.token);
          window.location = connectBackUrl.toString();
          return;
        } else if (['canva', 'mcp-server'].includes(serviceId)) {
          window.location = response.token;
        }
        setView('success');
      }
    });
  };

  return (
    <div className="layout layout-external-auth">
      <div className="external-auth-wrapper">
        <div className="external-auth-inner">
          <div className="login-logo">
            <img src={getLogoUrl()} alt="" />
          </div>
          {loading ? (
            <Loading />
          ) : (
            <>
              {error && (
                <div className="error center">
                  {errorMessage ? (
                    <span>{errorMessage}</span>
                  ) : (
                    <span>{intl.formatMessage({ defaultMessage: 'Authorization failed, please try again.' })}</span>
                  )}
                </div>
              )}
              {view === 'main' && (
                <>
                  {accounts.length === 0 ? (
                    <>
                      {!error && (
                        <div className="center">
                          {email ? (
                            <span>
                              {intl.formatMessage({
                                defaultMessage: `We couldn't find any Stockpress accounts associated with your email address ({email}).`,
                              }, { email })}
                            </span>
                          ) : (
                            <span>
                              {intl.formatMessage({
                                defaultMessage: `We couldn't find any Stockpress accounts associated with your email address.`,
                              })}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="center mt-3">
                        <a href="#email" onClick={() => { setError(false); setView('email'); }}>
                          {intl.formatMessage({ defaultMessage: 'Use a different email address' })}
                        </a>
                      </div>
                      {!getSetting('cs1') && (
                        <div className="center mt-3">
                          <Button onClick={() => { setError(false); setView('signup'); }}>
                            {intl.formatMessage({ defaultMessage: 'Create a free workspace' })}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="center">
                        {intl.formatMessage({
                          defaultMessage: `We found the following {integration} accounts associated with your email address ({email}):`,
                        }, { integration: integration.name, email })}
                      </div>
                      <div className="center mt-3">
                        <a href="#email" onClick={() => { setError(false); setView('email'); }}>
                          {intl.formatMessage({ defaultMessage: 'Use a different email address' })}
                        </a>
                      </div>
                      {!getSetting('cs1') && (
                        <div className="center mt-3">
                          <Button onClick={() => { setError(false); setView('signup'); }}>
                            {intl.formatMessage({ defaultMessage: 'Create a new free workspace' })}
                          </Button>
                        </div>
                      )}
                      <ExternalAuthAccountList
                        integration={integration}
                        accounts={accounts}
                        connectingUuid={connectingUuid}
                        handleConnect={handleConnect}
                      />
                    </>
                  )}
                </>
              )}
              {view === 'email' && (
                <>
                  <div className="center">
                    <h4>{intl.formatMessage({ defaultMessage: `Let's find your workspaces` })}</h4>
                    {intl.formatMessage({ defaultMessage: `We'll email you a code so we can identify your workspace URL(s).` })}
                  </div>
                  <Form className="mt-3" onSubmit={sendEmailValidationCode}>
                    <div className="center mt-3">
                      <Input
                        type="email"
                        name="email"
                        autoFocus
                        placeholder={intl.formatMessage({ defaultMessage: 'Enter your email address' })}
                        onChange={(e) => setEmailField(e.target.value)}
                      />
                    </div>
                    <div className="center mt-3">
                      <Button type="submit" disabled={sending}>
                        {sending ? intl.formatMessage({ defaultMessage: 'Wait...' }) :
                          intl.formatMessage({ defaultMessage: 'Get my code' })}
                      </Button>
                    </div>
                    {integration?.email_authentication && (
                      <div className="center mt-3">
                        <a href="#connect" onClick={() => { setView('main'); }}>
                          ← {intl.formatMessage({ defaultMessage: 'Back' })}
                        </a>
                      </div>
                    )}
                  </Form>
                </>
              )}
              {view === 'code' && (
                <>
                  <div className="center">
                    <h4>{intl.formatMessage({ defaultMessage: 'Check your email' })}</h4>
                    {intl.formatMessage({ defaultMessage: 'We sent you a confirmation code' })}
                  </div>
                  <Form className="mt-3" onSubmit={confirmCode}>
                    <div className="center mt-3">
                      <OtpInput
                        inputStyle="otp-input"
                        value={emailCode}
                        onChange={(c) => { setEmailCode(c); }}
                        numInputs={6}
                        shouldAutoFocus={true}
                        renderInput={(props) => <input {...props} />}
                      />
                    </div>
                    <div className="center mt-3">
                      <Button type="submit" disabled={loading}>
                        {loading ? intl.formatMessage({ defaultMessage: 'Wait...' }) :
                          intl.formatMessage({ defaultMessage: 'Confirm' })}
                      </Button>
                    </div>
                  </Form>
                </>
              )}
              {view === 'success' && (
                <>
                  <div className="center">
                    {['canva', 'mcp-server'].includes(serviceId) ? (
                      <>
                        {intl.formatMessage({
                          defaultMessage: `Selected account has been connected to {integration}.{br} You will be redirected back to {integration} shortly.`,
                        }, { integration: integration.name, br: <br /> })}
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({
                          defaultMessage: `Selected account has been connected to {integration}.{br} Now you can close this browser window.`,
                        }, { integration: integration.name, br: <br /> })}
                      </>
                    )}
                  </div>
                  {backUrl && (
                    <div className="center mt-4">
                      <a href={backUrl}>
                        <i className="far fa-arrow-left" />{' '}
                        {backText}
                      </a>
                    </div>
                  )}
                </>
              )}
              {view === 'signup' && (
                <ExternalAuthSignupForm
                  email={email}
                  integration={integration}
                  setView={setView}
                />
              )}
              {view === 'signed-up' && (
                <div className="center">
                  <h5 className="m-4">{intl.formatMessage({ defaultMessage: 'Thanks for signing up!' })}</h5>
                  <p>
                    {intl.formatMessage({
                      defaultMessage: `To activate your account please check your email for
                        your workspace link and to set your password.`,
                    })}
                  </p>
                  <p>
                    {intl.formatMessage({
                      defaultMessage: `If you do not see an email in a few minutes, please
                        check your spam folder.`,
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

ExternalAuth.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withRouter(connect(mapStateToProps)(ExternalAuth));
